import { Lock } from 'lucide-react';
import { useSalaryPassKey } from '@/hooks/payroll/useSalaryPassKey';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

interface Props {
  children: React.ReactNode;
  /** What is behind the lock, so the message names it. */
  label?: string;
}

/**
 * Hides a whole salary surface until the pass key is entered.
 *
 * Wraps the entire section rather than only the rate list: pay figures, bank
 * details and hourly rates are all the same class of information, and gating one
 * while the rest stays on screen protects nothing.
 *
 * Unlocking is shared for the session, so a person passing the gate on one screen
 * is not asked again on the next.
 */
export function SalaryGate({ children, label = 'Salary information' }: Props) {
  const { unlocked, requestUnlock } = useSalaryPassKey();

  if (unlocked) return <>{children}</>;

  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
        <div className="rounded-full bg-muted p-4">
          <Lock className="h-6 w-6 text-muted-foreground" />
        </div>
        <div className="space-y-1">
          <p className="font-medium">{label} is locked</p>
          <p className="text-sm text-muted-foreground">
            Enter the salary pass key to view it.
          </p>
        </div>
        <Button size="sm" onClick={requestUnlock}>Enter pass key</Button>
      </CardContent>
    </Card>
  );
}
